/**
 * SessionListDialog — Full-screen modal for browsing, reopening, and deleting sessions
 *
 * Fetches ALL sessions (including archived) and groups them by date.
 * Users can search, click to reopen a session as a tab, or permanently delete.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Search, Star, Trash2, X } from 'lucide-react'
import { useTheme, getThemeClass, useConfirm  } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { request } from '@papan/api-client'

interface SessionInfo {
  id: string
  title: string
  model: string
  time: {
    created: number
    updated: number
    archived?: number
    /** When the member starred it — "keep this". #758 part 2. */
    starred?: number
  }
}

interface Props {
  workspaceId: string
  openTabIds: string[]
  onOpen: (sessionId: string) => void
  onClose: () => void
  onDelete?: (sessionId: string) => void
}

type DateGroup = 'today' | 'yesterday' | 'thisWeek' | 'older'

function getDateGroup(timestamp: number): DateGroup {
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const startOfYesterday = startOfToday - 86400000
  const startOfWeek = startOfToday - now.getDay() * 86400000

  if (timestamp >= startOfToday) return 'today'
  if (timestamp >= startOfYesterday) return 'yesterday'
  if (timestamp >= startOfWeek) return 'thisWeek'
  return 'older'
}

function groupByDate(sessions: SessionInfo[]): Map<DateGroup, SessionInfo[]> {
  const groups = new Map<DateGroup, SessionInfo[]>()
  for (const s of sessions) {
    const group = getDateGroup(s.time.updated)
    const list = groups.get(group) ?? []
    list.push(s)
    groups.set(group, list)
  }
  return groups
}

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

const GROUP_ORDER: DateGroup[] = ['today', 'yesterday', 'thisWeek', 'older']

export function SessionListDialog({ workspaceId, openTabIds, onOpen, onClose, onDelete }: Props) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const { t } = useTranslation('agent')
  const confirm = useConfirm()

  const [sessions, setSessions] = useState<SessionInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)

  const openSet = new Set(openTabIds)

  const fetchSessions = useCallback(async (searchQuery?: string) => {
    try {
      let url = `/api/agent/sessions?workspaceId=${encodeURIComponent(workspaceId)}&includeArchived=true`
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`
      const data = await request<SessionInfo[]>(url)
      setSessions(data)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  // Initial load
  useEffect(() => { fetchSessions() }, [fetchSessions])

  // Debounced search
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search)
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [search])

  useEffect(() => {
    fetchSessions(debouncedSearch || undefined)
  }, [debouncedSearch, fetchSessions])

  // Auto-focus search input
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const handleDelete = async (session: SessionInfo) => {
    const confirmed = await confirm({
      title: t('session.deleteConfirmTitle'),
      message: t('session.deleteConfirmMessage', { title: session.title || 'Untitled' }),
      type: 'danger',
      confirmText: t('session.delete'),
    })
    if (!confirmed) return

    try {
      await request(
        `/api/agent/sessions/${session.id}?workspaceId=${encodeURIComponent(workspaceId)}`,
        { method: 'DELETE' },
      )
      setSessions(prev => prev.filter(s => s.id !== session.id))
      onDelete?.(session.id)
    } catch {
      // ignore
    }
  }

  /**
   * Star or unstar — the member's own "keep this" (#758 part 2).
   *
   * An admin's Force cleanup selects conversations by IDLE AGE, which is
   * a good rule and a blind one: the conversation nobody has touched for
   * ninety days may be the signed contract. A star is how somebody says
   * so in advance, and the cleanup passes over it unless the admin
   * explicitly ticks the override.
   *
   * ⚠️ Starring does NOT make a conversation look active. The server
   * holds `time_updated` deliberately (see `Session.setStarred`), so the
   * row's "3 weeks ago" does not move when a star goes on — that is
   * correct, not a bug to fix here. A star that reset the clock would
   * also escape the 12-month retention the privacy page promises.
   *
   * Optimistic, and reverted on failure: this is a one-click toggle on a
   * list somebody is scanning, and a star that silently did not save is
   * worse than a star that flickers.
   */
  const toggleStar = async (session: SessionInfo) => {
    const next = !session.time.starred
    setSessions(prev => prev.map(s => (
      s.id === session.id ? { ...s, time: { ...s.time, starred: next ? Date.now() : undefined } } : s
    )))
    try {
      await request(
        `/api/agent/sessions/${session.id}?workspaceId=${encodeURIComponent(workspaceId)}`,
        { method: 'PATCH', body: JSON.stringify({ starred: next }) },
      )
    } catch {
      setSessions(prev => prev.map(s => (
        s.id === session.id ? { ...s, time: { ...s.time, starred: session.time.starred } } : s
      )))
    }
  }

  const grouped = groupByDate(sessions)
  const groupLabels: Record<DateGroup, string> = {
    today: t('session.today'),
    yesterday: t('session.yesterday'),
    thisWeek: t('session.thisWeek'),
    older: t('session.older'),
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />

      {/* Frost-glass modal (DESIGN_DOC §10) */}
      <div className={`${themeClass} ws-glass-modal ws-anim-modal-in relative w-full max-w-lg mx-4 flex flex-col max-h-[70vh]`}>
        {/* Header */}
        <div className="flex items-center gap-2 px-4 pt-4 pb-2">
          <h2 className="text-sm font-medium themed-text flex-1">{t('session.history')}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/10 themed-text-muted hover:themed-text transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 ws-drive-pill rounded-lg px-3 py-1.5">
            <Search size={14} className="themed-text-muted shrink-0" />
            <input
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('session.searchPlaceholder')}
              className="flex-1 bg-transparent text-sm themed-text outline-none placeholder:themed-text-muted"
            />
          </div>
        </div>

        {/* Session list */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 min-h-0">
          {loading ? (
            <div className="text-center py-8 text-sm themed-text-muted">Loading...</div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8 text-sm themed-text-muted">
              {debouncedSearch ? t('session.noResults') : t('session.noSessions')}
            </div>
          ) : (
            GROUP_ORDER.map(group => {
              const items = grouped.get(group)
              if (!items || items.length === 0) return null
              return (
                <div key={group} className="mb-3">
                  <div className="text-[10px] uppercase tracking-wider themed-text-muted mb-1 px-1">
                    {groupLabels[group]}
                  </div>
                  {items.map(session => {
                    const isOpen = openSet.has(session.id)
                    return (
                      <div
                        key={session.id}
                        onClick={() => onOpen(session.id)}
                        className="flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer hover:bg-white/[0.07] group transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm themed-text truncate">
                            {session.title || 'Untitled'}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] themed-text-muted bg-brand-500/10 text-brand-400 px-1.5 py-0.5 rounded">
                              {session.model.split('/').pop() || session.model}
                            </span>
                            <span className="text-[10px] themed-text-muted">
                              {formatRelativeTime(session.time.updated)}
                            </span>
                          </div>
                        </div>
                        {isOpen && (
                          <span className="text-[10px] themed-text-muted ws-glass-chip rounded px-1.5 py-0.5 shrink-0">
                            Go To
                          </span>
                        )}
                        {/* A STARRED ROW KEEPS ITS STAR VISIBLE. The
                            delete button appears on hover because it is
                            destructive and rare; a star is state, and
                            state that only exists while the pointer is
                            over it cannot be scanned down a list — which
                            is exactly what somebody does before an
                            admin's cleanup runs. */}
                        <button
                          onClick={e => { e.stopPropagation(); void toggleStar(session) }}
                          className={`p-1 rounded shrink-0 transition-opacity hover:bg-amber-500/10 ${
                            session.time.starred ? 'text-amber-400 opacity-100' : 'themed-text-muted opacity-0 group-hover:opacity-100'
                          }`}
                          title={t(session.time.starred ? 'session.unstar' : 'session.star')}
                          aria-pressed={!!session.time.starred}
                        >
                          <Star size={14} fill={session.time.starred ? 'currentColor' : 'none'} />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); handleDelete(session) }}
                          className="p-1 rounded hover:bg-red-500/10 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          title={t('session.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
